import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export default function FullscreenViewer({
  isFullscreen,
  currentProject,
  showInfo,
  setShowInfo,
  closeFullscreen,
  mapCategoryToType,
  // Video player props
  videoRef,
  videoContainerRef,
  isIdle,
  isVideoPlaying,
  isMuted,
  volume,
  currentTime,
  duration,
  progress,
  showVolumeSlider,
  isDragging,
  handleVideoPlay,
  handleVideoPause,
  handleVideoEnded,
  handleTimeUpdate,
  handleLoadedMetadata,
  togglePlayPause,
  toggleMute,
  handleVolumeChange,
  toggleVolumeSlider,
  formatTime,
  handleSeekBarMouseDown,
  // Image gallery props
  showSingleImage,
  currentImageIndex,
  viewSingleImage,
  navigateImage,
  setIsImageLoading,
  setShowSingleImage,
  setCurrentImageIndex,
  // Navigation props
  navigateToProject,
  isNavigating,
  currentProjectIndex,
  filteredProjects,
}) {
  // Gallery scroll state
  const [showScrollUp, setShowScrollUp] = React.useState(false)
  const [showScrollDown, setShowScrollDown] = React.useState(false)
  const galleryScrollRef = React.useRef(null)

  // Handle gallery scroll
  const handleGalleryScroll = () => {
    if (galleryScrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = galleryScrollRef.current
      setShowScrollUp(scrollTop > 100)
      setShowScrollDown(scrollTop + clientHeight < scrollHeight - 100)
    }
  }

  const scrollGallery = (direction) => {
    if (galleryScrollRef.current) {
      const scrollAmount = 400
      const currentScroll = galleryScrollRef.current.scrollTop
      const targetScroll = direction === 'up' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount
      
      galleryScrollRef.current.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      })
    }
  }

  // Check scroll on mount and when gallery changes
  React.useEffect(() => {
    if (galleryScrollRef.current && !showSingleImage && mapCategoryToType(currentProject?.category) === 'graphic-design') {
      handleGalleryScroll()
    }
  }, [showSingleImage, currentProject])
  // Function to check if URL is a YouTube video
  const isYouTubeUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'))
  }

  // Function to check if URL is a Cloudinary video
  const isCloudinaryUrl = (url) => {
    return url && url.includes('cloudinary.com')
  }

  // Function to check if URL is a video file
  const isVideoFile = (url) => {
    if (!url) return false
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv']
    return videoExtensions.some(ext => url.toLowerCase().includes(ext))
  }

  // Function to convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return ''
    
    let videoId = ''
    
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0]
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0]
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0]
    }
    
    if (videoId) {
      // Use more permissive parameters for better compatibility
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${isMuted ? 1 : 0}&controls=1&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${window.location.origin}`
    }
    
    return url
  }
  if (!isFullscreen || !currentProject) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black z-50 flex font-syne"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeFullscreen()
          }
        }}
      >
        {/* Top Navigation Bar */}
        <motion.div
          animate={{
            opacity: isIdle ? 0 : 1,
            y: isIdle ? -20 : 0,
            width: showInfo ? '70%' : '100%',
          }}
          transition={{
            duration: isIdle ? 0.5 : 0.4,
            ease: 'easeInOut',
          }}
          className="absolute top-0 left-0 z-30 flex items-center p-6"
        >
          {/* Left: Back To Home - Hidden for Graphic Design projects */}
          {currentProject.category !== 'graphic-design' && (
              <button
                onClick={closeFullscreen}
                  className="text-lg font-semibold hover:text-gray-300 transition-colors"
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  lineHeight: '1.1',
                  textAlign: 'left',
                  color: 'white',
                }}
              >
                back
                <br />
                to home
              </button>
          )}

          {/* Right: Info Button - Always visible when info bar is closed */}
          <div className="ml-auto">
            {!showInfo && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowInfo(true)
                    }}
                    className="text-lg font-semibold hover:text-gray-300 transition-colors font-syne"
                    style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'white',
                    }}
                  >
                    info
                  </button>
            )}
          </div>
        </motion.div>

        {/* Shortcuts Panel - Bottom Left */}
        {mapCategoryToType(currentProject.category) === 'video-editing' && (
          <motion.div
            animate={{
              opacity: isIdle ? 0 : 1,
              y: isIdle ? 10 : 0,
            }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-4 left-6 z-[55] hidden sm:block"
          >
            <div className="bg-black/60 backdrop-blur-sm border border-white/10 rounded-lg px-3 py-2 text-white/90 font-syne">
              <div className="text-[10px] tracking-wide uppercase text-white/70 mb-1">Shortcuts</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                <div className="flex items-center gap-1">
                  <span className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10">Space</span>
                  <span className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10">K</span>
                  <span className="text-white/70">Play/Pause</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10">M</span>
                  <span className="text-white/70">Mute</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10">J</span>
                  <span className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10">←</span>
                  <span className="text-white/70">−5s</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10">L</span>
                  <span className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10">→</span>
                  <span className="text-white/70">+5s</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10">↑</span>
                  <span className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10">↓</span>
                  <span className="text-white/70">Volume ±5%</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Sound Controls - Only for Video Projects (desktop/tablet only) */}
        {mapCategoryToType(currentProject.category) === 'video-editing' && (
          <motion.div
            animate={{
              left: '50%',
              opacity: isIdle ? 0 : 1,
              y: isIdle ? -20 : 0,
            }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className="absolute sm:top-4 left-1/2 transform -translate-x-1/2 z-[60] p-3 sm:p-4 pointer-events-none hidden sm:block"
          >
            <div className="flex flex-col items-center text-center pointer-events-auto"
              onWheel={(e) => {
                // Smooth wheel volume control
                const delta = e.deltaY > 0 ? -0.05 : 0.05
                const next = Math.min(1, Math.max(0, (isMuted ? 0 : volume) + delta))
                handleVolumeChange(parseFloat(next.toFixed(2)))
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  isIdle ? toggleVolumeSlider() : toggleMute()
                }}
                className="text-base sm:text-lg font-semibold hover:text-gray-300 transition-colors mb-1 sm:mb-2 font-syne"
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                }}
              >
                {isIdle ? (
                  <span>
                    {formatTime(currentTime)}/{formatTime(duration)}
                  </span>
                ) : (
                  `SOUND ${isMuted ? 'ON' : 'OFF'}`
                )}
              </button>

              {/* Volume Slider */}
              {(!isIdle || showVolumeSlider) && (
                <div className="flex items-center space-x-2 mt-1 sm:mt-2 justify-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleMute()
                    }}
                    className="text-white hover:text-[#E06100] transition-colors"
                  >
                    {isMuted || volume === 0 ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                      </svg>
                    )}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                    className="w-28 sm:w-36 h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer volume-slider outline-none focus:outline-none focus:ring-0"
                    style={{
                      background: `linear-gradient(to right, #E06100 0%, #E06100 ${(isMuted ? 0 : volume) * 100}%, #374151 ${(isMuted ? 0 : volume) * 100}%, #374151 100%)`,
                      accentColor: 'transparent',
                    }}
                  />
                  <span className="text-white text-[10px] sm:text-xs min-w-[2rem] text-center">
                    {Math.round((isMuted ? 0 : volume) * 100)}%
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Project Container */}
        <motion.div
          animate={{
            width: showInfo ? '70%' : '100%',
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className="relative h-full"
        >
          {/* Video Player */}
          {mapCategoryToType(currentProject.category) === 'video-editing' && (
            <div
              ref={videoContainerRef}
              style={{ position: 'relative', width: '100%', height: '100%' }}
            >
              {isYouTubeUrl(currentProject.projectUrl) ? (
                <iframe
                  src={getYouTubeEmbedUrl(currentProject.projectUrl)}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  title={`YouTube Video: ${currentProject.title}`}
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : (
                <video
                  ref={videoRef}
                  src={
                    currentProject.projectUrl ||
                    currentProject.videoFile?.url ||
                    '/Himachal Pradesh - Mission.mp4'
                  }
                  className="w-full h-full object-contain"
                  onPlay={handleVideoPlay}
                  onPause={handleVideoPause}
                  onEnded={handleVideoEnded}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  preload="metadata"
                  playsInline
                  muted={isMuted}
                  autoPlay
                  onClick={togglePlayPause}
                />
              )}

              {/* Center Play/Pause Button (clickable, above seek overlay) */}
              <motion.div
                animate={{ opacity: isIdle ? 0 : 1 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    togglePlayPause()
                  }}
                  className="rounded-full bg-black/40 border border-white/20 text-white px-6 py-3 hover:bg-black/60 transition-colors pointer-events-auto"
                  style={{
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)'
                  }}
                >
                  <span className="text-lg font-semibold">
                    {isVideoPlaying ? 'Pause' : 'Play'}
                  </span>
                </button>
              </motion.div>

              {/* Time Display - Center Left and Right (desktop/tablet only) */}
              <motion.div
                animate={{
                  opacity: isIdle ? 0 : 1,
                }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="absolute inset-0 hidden sm:flex items-center justify-between px-6 pointer-events-none"
              >
                <span
                  className="text-white text-lg font-semibold font-syne"
                  style={{ fontSize: '18px', fontWeight: '600' }}
                >
                  {formatTime(currentTime)}
                </span>
                <span
                  className="text-white text-lg font-semibold font-syne"
                  style={{ fontSize: '18px', fontWeight: '600' }}
                >
                  {formatTime(duration)}
                </span>
              </motion.div>

              {/* Advanced Seek Overlay and Progress Indicator (desktop/tablet only) */}
              <motion.div
                animate={{
                  opacity: isIdle ? 0 : 1,
                }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="absolute inset-0 hidden sm:block"
              >
                {/* Full-width invisible overlay to click/drag anywhere to seek */}
                <div
                  className="absolute inset-0 pointer-events-auto"
                  style={{ cursor: isDragging ? 'grabbing' : 'pointer' }}
                  onMouseDown={handleSeekBarMouseDown}
                />

                {/* Visual Line - 2px white line positioned at progress */}
                <div
                  className="absolute top-0 bottom-0 bg-white pointer-events-none"
                  style={{
                    left: `${progress}%`,
                    width: '2px',
                    transform: 'translateX(-50%)',
                    transition: isDragging ? 'none' : 'left 300ms ease-out',
                  }}
                />
                {/* Enlarged draggable handle/hit area around the line */}
                <div
                  className="absolute top-0 bottom-0 bg-transparent pointer-events-none"
                  style={{
                    left: `${progress}%`,
                    width: '48px',
                    transform: 'translateX(-50%)',
                  }}
                />
              </motion.div>

              {/* Mobile Seekbar + Time (simple controls for small screens) */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-[90%] flex flex-col items-center sm:hidden z-[60]">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={Number.isFinite(progress) ? progress : 0}
                  onChange={(e) => {
                    const newProgress = parseFloat(e.target.value)
                    if (videoRef.current && videoRef.current.duration) {
                      const newTime = (newProgress / 100) * videoRef.current.duration
                      videoRef.current.currentTime = newTime
                    }
                  }}
                  className="w-full h-1 rounded-full appearance-none cursor-pointer bg-gray-700/80 overflow-hidden"
                  style={{
                    background: `linear-gradient(to right, #E06100 0%, #E06100 ${progress || 0}%, #374151 ${progress || 0}%, #374151 100%)`,
                  }}
                />
                <div className="flex justify-between w-full text-[11px] text-white mt-1 font-syne">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Mobile Volume Bar - simple slider at top center with +/- icons */}
              <div className="absolute top-16 left-1/2 transform -translate-x-1/2 w-[35%] sm:hidden z-[60]">
                <div className="flex items-center space-x-2">
                  {/* Decrease volume */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      const next = Math.max(0, (isMuted ? 0 : volume) - 0.1)
                      handleVolumeChange(parseFloat(next.toFixed(2)))
                    }}
                    className="text-white hover:text-[#E06100] transition-colors flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
                    </svg>
                  </button>

                  {/* Volume slider */}
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                    className="w-full h-1 rounded-full appearance-none cursor-pointer bg-gray-700/80 overflow-hidden"
                    style={{
                      background: `linear-gradient(to right, #E06100 0%, #E06100 ${(isMuted ? 0 : volume) * 100}%, #374151 ${(isMuted ? 0 : volume) * 100}%, #374151 100%)`,
                    }}
                  />

                  {/* Increase volume */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      const next = Math.min(1, (isMuted ? 0 : volume) + 0.1)
                      handleVolumeChange(parseFloat(next.toFixed(2)))
                    }}
                    className="text-white hover:text-[#E06100] transition-colors flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Graphic Design Gallery */}
          {mapCategoryToType(currentProject.category) === 'graphic-design' && (
            <div className="relative h-full overflow-hidden">
              {/* Dynamic Back Button - Top Level */}
              <button
                onClick={() => {
                  if (showSingleImage) {
                    setShowSingleImage(false)
                    setCurrentImageIndex(0)
                  } else {
                    closeFullscreen()
                  }
                }}
                className="absolute top-6 left-6 z-[9999] text-lg font-semibold hover:text-gray-300 transition-colors cursor-pointer font-syne"
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  lineHeight: '1.1',
                  textAlign: 'left',
                  color: 'white',
                }}
              >
                back
                <br />
                {showSingleImage ? 'to gallery' : 'to projects'}
              </button>
              
              {!showSingleImage ? (
                /* Creative Masonry Gallery */
                <div 
                  ref={galleryScrollRef}
                  onScroll={handleGalleryScroll}
                  className="w-full h-full overflow-y-auto relative gallery-scroll-container"
                >
                  <div className="w-full p-8 pt-24 relative z-10">
                    <div className="max-w-6xl mx-auto">
                      {/* Gallery Title */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                      >
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                          {currentProject.title}
                        </h2>
                        <p className="text-[#E06100] text-sm font-medium">
                          {currentProject.client}
                        </p>
                      </motion.div>

                      {/* Dynamic Masonry Layout */}
                      <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
                        {currentProject.galleryImages?.map((imageUrl, index) => {
                          const heights = ['h-64', 'h-80', 'h-96', 'h-72', 'h-88']
                          const randomHeight = heights[index % heights.length]

                          return (
                            <motion.div
                              key={index}
                              initial={{
                                opacity: 0,
                                scale: 0.8,
                                rotateY: -15,
                                z: -100,
                              }}
                              animate={{
                                opacity: 1,
                                scale: 1,
                                rotateY: 0,
                                z: 0,
                              }}
                              transition={{
                                duration: 0.8,
                                delay: index * 0.1,
                                ease: [0.25, 0.46, 0.45, 0.94],
                              }}
                              whileHover={{
                                scale: 1.05,
                                rotateY: 5,
                                z: 50,
                                transition: { duration: 0.3 },
                              }}
                              className="group cursor-pointer break-inside-avoid mb-6 relative"
                              style={{ transformStyle: 'preserve-3d' }}
                              onClick={() => viewSingleImage(index)}
                            >
                              {/* Glass Card Effect */}
                              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 shadow-2xl">
                                {/* Image Container */}
                                <div className={`relative ${randomHeight} overflow-hidden rounded-2xl`}>
                                  <img
                                    src={imageUrl}
                                    alt={`Gallery image ${index + 1}`}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                  />

                                  {/* Clean Overlay */}
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                                    <div className="absolute bottom-4 right-4">
                                      <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                                        <svg
                                          className="w-5 h-5 text-white"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                          />
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                          />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Shine Effect */}
                                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                </div>
                              </div>
                            </motion.div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Scroll Navigation Arrows */}
                  <AnimatePresence>
                    {showScrollUp && (
                      <motion.button
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ 
                          opacity: 1, 
                          y: 0,
                          right: showInfo ? 'calc(30% + 2rem)' : '2rem'
                        }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => scrollGallery('up')}
                        className="fixed top-24 z-50 w-12 h-12 bg-black/80 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-[#E06100] hover:border-[#E06100] transition-colors duration-300 shadow-lg"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </motion.button>
                    )}
                    {showScrollDown && (
                      <motion.button
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ 
                          opacity: 1, 
                          y: 0,
                          right: showInfo ? 'calc(30% + 2rem)' : '2rem'
                        }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => scrollGallery('down')}
                        className="fixed bottom-8 z-50 w-12 h-12 bg-black/80 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-[#E06100] hover:border-[#E06100] transition-colors duration-300 shadow-lg"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                /* Single Image View */
                <div className="w-full h-full flex items-center justify-center p-8">
                  <div className="relative max-w-7xl w-full max-h-full">
                    <motion.div
                      key={currentImageIndex}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.4 }}
                      className="relative"
                    >
                      <img
                        src={currentProject.galleryImages[currentImageIndex]}
                        alt={`Gallery image ${currentImageIndex + 1}`}
                        className="w-full h-auto max-h-[80vh] object-contain rounded-2xl"
                        onLoad={() => setIsImageLoading(false)}
                      />
                    </motion.div>

                    {/* Navigation Arrows */}
                    {currentImageIndex > 0 && (
                      <button
                        onClick={() => navigateImage('prev')}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/80 border border-gray-600 rounded-full flex items-center justify-center text-white hover:bg-[#E06100] hover:border-[#E06100] transition-all duration-300"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                    )}

                    {currentImageIndex < currentProject.galleryImages.length - 1 && (
                      <button
                        onClick={() => navigateImage('next')}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/80 border border-gray-600 rounded-full flex items-center justify-center text-white hover:bg-[#E06100] hover:border-[#E06100] transition-all duration-300"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}

                    {/* Counter */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-40 bg-black/80 border border-gray-600 rounded-lg px-3 py-1 text-white text-sm">
                      <span className="text-[#E06100]">{currentImageIndex + 1}</span>
                      <span className="text-gray-400 mx-1">of</span>
                      <span>{currentProject.galleryImages.length}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Website Live Preview */}
          {mapCategoryToType(currentProject.category) === 'website' && (
            <div className="w-full h-full flex items-center justify-center p-4 sm:p-8 pt-16 sm:pt-20">
              {/* Project Navigation Arrows */}
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-40">
                <button
                  onClick={() => navigateToProject('prev')}
                  disabled={isNavigating}
                  className="w-12 h-12 bg-black/80 border border-gray-600 rounded-full flex items-center justify-center text-white hover:bg-[#E06100] hover:border-[#E06100] transition-all duration-300 disabled:opacity-50 group"
                >
                  <svg className="w-6 h-6 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              </div>

              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 z-40">
                <button
                  onClick={() => navigateToProject('next')}
                  disabled={isNavigating}
                  className="w-12 h-12 bg-black/80 border border-gray-600 rounded-full flex items-center justify-center text-white hover:bg-[#E06100] hover:border-[#E06100] transition-all duration-300 disabled:opacity-50 group"
                >
                  <svg className="w-6 h-6 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="max-w-7xl w-full max-h-full">
                <motion.div
                  key={currentProject.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                  className="relative"
                >
                  <div className="bg-black border border-gray-800 rounded-2xl p-4 sm:p-6 shadow-2xl">
                    {/* Header */}
                    <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-6">
                      <div className="flex-1">
                        <h3 className="text-white text-xl sm:text-2xl font-bold mb-2">
                          {currentProject.title}
                        </h3>
                        <p className="text-[#E06100] text-sm font-medium uppercase tracking-wider mb-3 sm:mb-0">
                          {currentProject.client} • Web Development
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                        <div className="text-left sm:text-right">
                          <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">
                            Status
                          </div>
                          <div className="text-green-400 text-sm font-semibold">Live</div>
                        </div>
                        <a
                          href={currentProject.projectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 sm:px-6 py-2 sm:py-3 bg-[#E06100] text-white rounded-lg hover:bg-[#E06100]/90 transition-all duration-300 flex items-center justify-center space-x-2 font-medium hover:scale-105 text-sm sm:text-base"
                        >
                          <span>Visit Live Site</span>
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>

                    {/* Browser Frame */}
                    <div className="bg-gray-900 rounded-lg overflow-hidden">
                      {/* Browser Header */}
                      <div className="bg-gray-800 px-3 sm:px-4 py-2 sm:py-3 flex items-center space-x-2 sm:space-x-3">
                        <div className="flex space-x-1.5 sm:space-x-2">
                          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full"></div>
                          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-yellow-500 rounded-full"></div>
                          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                        </div>
                        <div className="flex-1 bg-gray-700 rounded-md px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-300 font-mono truncate">
                          {currentProject.projectUrl}
                        </div>
                      </div>

                      {/* Website Content */}
                      <div className="relative bg-white" style={{ minHeight: '600px' }}>
                        <iframe
                          src={currentProject.projectUrl}
                          className="w-full h-full"
                          style={{ minHeight: '600px' }}
                          title={`Live preview of ${currentProject.title}`}
                          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Project Counter */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-40">
                <div className="bg-black/80 border border-gray-600 rounded-full px-4 py-2 text-white text-sm">
                  {currentProjectIndex + 1} of{' '}
                  {filteredProjects.filter((p) => mapCategoryToType(p.category) === 'website').length}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Project Info Bar */}
        <AnimatePresence>
          {showInfo && (
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className="font-syne w-full md:w-[40%] lg:w-[30%]"
              style={{
                position: 'fixed',
                top: '0',
                right: '0',
                height: '100vh',
                backgroundColor: '#000000',
                zIndex: '99999',
                padding: '24px',
                overflow: 'auto',
                borderLeft: '1px solid #333',
              }}
            >
              <div className="max-w-md mx-auto">
                {/* Close Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowInfo(false)
                  }}
                  className="font-syne"
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    transition: 'color 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#cccccc'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'white'
                  }}
                >
                  ✕
                </button>

                {/* Project Title */}
                <h1
                  style={{
                    fontSize: '22px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    color: 'white',
                    lineHeight: '1.3',
                    textAlign: 'center',
                  }}
                >
                  {currentProject.title}
                </h1>

                {/* Client */}
                <p
                  style={{
                    fontSize: '13px',
                    color: '#888888',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '24px',
                    textAlign: 'center',
                  }}
                >
                  {currentProject.client}
                </p>

                {/* Description */}
                <div style={{ marginBottom: '24px' }}>
                  <h3
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#E06100',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      marginBottom: '12px',
                      textAlign: 'center',
                    }}
                  >
                    About
                  </h3>
                  <p
                    style={{
                      fontSize: '14px',
                      lineHeight: '1.6',
                      color: '#cccccc',
                      margin: '0',
                      textAlign: 'center',
                    }}
                  >
                    {currentProject.description}
                  </p>
                </div>

                {/* Project Details */}
                <div style={{ marginBottom: '24px' }}>
                  <h3
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#E06100',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      marginBottom: '12px',
                      textAlign: 'center',
                    }}
                  >
                    Project Details
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#888888', fontSize: '13px' }}>Category</span>
                      <span style={{ color: '#cccccc', fontSize: '13px', fontWeight: '500' }}>
                        {currentProject.category === 'graphic-design'
                          ? 'Graphic Design'
                          : mapCategoryToType(currentProject.category) === 'video-editing'
                            ? 'Video Editing'
                            : mapCategoryToType(currentProject.category) === 'website'
                              ? 'Website'
                              : currentProject.category}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#888888', fontSize: '13px' }}>Duration</span>
                      <span style={{ color: '#cccccc', fontSize: '13px', fontWeight: '500' }}>
                        {currentProject.duration || 'N/A'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#888888', fontSize: '13px' }}>Date</span>
                      <span style={{ color: '#cccccc', fontSize: '13px', fontWeight: '500' }}>
                        {currentProject.projectDate
                          ? new Date(currentProject.projectDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })
                          : 'N/A'}
                      </span>
                    </div>
                    {/* Live Posted Link for Video Projects */}
                    {mapCategoryToType(currentProject.category) === 'video-editing' && currentProject.liveLink && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <a
                          href={currentProject.liveLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#E06100',
                            fontSize: '13px',
                            fontWeight: '500',
                            textDecoration: 'none',
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          View Post →
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Services Provided */}
                <div style={{ marginBottom: '24px' }}>
                  <h3
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#E06100',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      marginBottom: '12px',
                      textAlign: 'center',
                    }}
                  >
                    SERVICES
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                    {currentProject.technologies?.map((tech, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '8px 16px',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: '#cccccc',
                          borderRadius: '20px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontWeight: '500',
                        }}
                      >
                        {tech.technology}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}
